import type { ReactElement, ReactNode } from 'react';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import Popover, { type PopoverProps } from './Popover';

const tooltipVariant = cva('px-4 py-2 text-sm text-white', {
    variants: {
        variant: {
            normal: 'rounded-xs bg-black',
            table: 'rounded-lg bg-gray-900',
        },
    },
    defaultVariants: {
        variant: 'normal',
    },
});

type TooltipProps = Pick<PopoverProps, 'as' | 'event'> & {
    /** @default 'span' */
    as?: PopoverProps['as'];
    /** @default 'hover' */
    event?: PopoverProps['event'];
    position?: PopoverProps['position'];
    /** @default true */
    enabled?: boolean;
    /** @default 'normal' */
    variant?: VariantProps<typeof tooltipVariant>['variant'];
    children: ReactElement;
    /** Styles the tooltip bubble — NOT the element it is attached to. See `triggerClassName`. */
    className?: string;
    /**
     * Styles the wrapper around `children`. Needed for layout: the wrapper is a flex/grid item
     * like any other, so without a width it shrinks to its content and a full-width control
     * inside it comes out narrower than its unwrapped siblings.
     */
    triggerClassName?: string;
    content: ReactNode;
    /** Delay in ms before showing the tooltip */
    delay?: number;
};

export default function Tooltip({
    as = 'span',
    event = 'hover',
    position = 'top',
    content,
    enabled = true,
    variant = 'normal',
    className,
    triggerClassName,
    delay,
    children,
}: TooltipProps): ReactElement {
    if (!enabled) return children;

    return (
        <Popover
            as={as}
            className={triggerClassName}
            event={event}
            position={position}
            delay={delay}
            hasArrow
            querySelector="#tooltips"
            arrowClassName="tooltip-arrow bg-black"
            content={
                <div className={cx(className ?? '', tooltipVariant({ variant }))}>{content}</div>
            }
        >
            {children}
        </Popover>
    );
}
